import React, { useEffect, useState, useMemo } from 'react';
import { Loader2, Save, MapPin } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const PRODUCTS = [
  { id: 'vpower', label: 'V-Power' },
  { id: 'super', label: 'Súper' },
  { id: 'regular', label: 'Regular' },
  { id: 'diesel', label: 'Diesel' },
  { id: 'ulsd', label: 'ULSD' },
];

const emptyAdj = () => PRODUCTS.reduce((a, p) => ({ ...a, [p.id]: { adjustment: 0, auto_full_diff: 0 } }), {});

const AdminStationPricing = () => {
  const [stations, setStations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState('');
  const [dept, setDept] = useState('');
  const [enabled, setEnabled] = useState([]);
  const [adj, setAdj] = useState(emptyAdj());
  const [minHours, setMinHours] = useState(24);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    Promise.all([api.get('/stations'), api.get('/departments')])
      .then(([s, d]) => { setStations(s.data); setDepartments(d.data); })
      .finally(() => setLoading(false));
  }, []);

  const ownStations = useMemo(() => (stations || []).filter(s => s.is_own || ['UNO', 'Shell'].includes(s.brand)), [stations]);
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return ownStations.filter(s => !q || s.name.toLowerCase().includes(q) || s.brand.toLowerCase().includes(q));
  }, [ownStations, search]);
  const selected = stations.find(s => s.id === selectedId);

  useEffect(() => {
    if (!selected) return;
    setDept(selected.department_id || '');
    setEnabled((selected.available_products && selected.available_products.length > 0) ? selected.available_products : (selected.enabled_products || PRODUCTS.map(p => p.id)));
    const base = emptyAdj();
    Object.entries(selected.product_adjustments || {}).forEach(([pid, v]) => {
      base[pid] = { adjustment: v.adjustment || 0, auto_full_diff: v.auto_full_diff || 0 };
    });
    setAdj(base);
    setMinHours(selected.min_hours_between_changes || 24);
  }, [selectedId, stations]);  // eslint-disable-line

  const save = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await api.patch(`/stations/${selectedId}/pricing`, {
        department_id: dept || null,
        enabled_products: enabled,
        product_adjustments: adj,
        min_hours_between_changes: Number(minHours) || 24,
      });
      toast.success('Configuración guardada');
      const s = await api.get('/stations');
      setStations(s.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const toggleProduct = (pid) => setEnabled(e => e.includes(pid) ? e.filter(x => x !== pid) : [...e, pid]);

  const deptDoc = departments.find(d => d.id === dept);

  return (
    <div className="space-y-6" data-testid="admin-station-pricing-page">
      <div>
        <h2 className="font-serif text-[36px] leading-tight tracking-tight">Precios por estación</h2>
        <p className="text-[13px] text-[#5a5a5a] mt-1">Configura departamento, productos, ajustes específicos y ventana de cambio por estación propia (UNO/Shell).</p>
      </div>

      <div className="grid md:grid-cols-[320px_1fr] gap-6">
        {/* Left: station picker */}
        <div className="bg-white border border-[#e8e5de] rounded-xl p-3 space-y-2 max-h-[650px] overflow-y-auto">
          <Input placeholder="Buscar estación…" value={search} onChange={e => setSearch(e.target.value)} className="bg-[#fafaf6] h-9 text-[13px]" data-testid="input-search-station"/>
          {loading ? <Loader2 className="animate-spin text-[#8a8579] mx-auto my-6"/> : filtered.map(s => (
            <button
              key={s.id}
              onClick={() => setSelectedId(s.id)}
              className={`w-full text-left px-3 py-2 rounded-lg transition ${selectedId === s.id ? 'bg-[#1c1c1c] text-[#f8f6f1]' : 'hover:bg-[#f3f0e9]'}`}
              data-testid={`station-pick-${s.id}`}
            >
              <div className="flex items-center justify-between">
                <p className="text-[12.5px] font-medium leading-tight">{s.name}</p>
                <span className={`text-[10px] ${selectedId === s.id ? 'text-[#cfcabe]' : 'text-[#8a8579]'}`}>{s.brand}</span>
              </div>
              <p className={`text-[10.5px] ${selectedId === s.id ? 'text-[#cfcabe]' : 'text-[#8a8579]'}`}>
                {s.department_id ? (departments.find(d => d.id === s.department_id)?.name || '—') : 'Sin depto'}
              </p>
            </button>
          ))}
        </div>

        {/* Right: form */}
        {!selected ? (
          <div className="bg-white border border-[#e8e5de] rounded-xl p-10 text-center text-[13px] text-[#8a8579]">
            Selecciona una estación de la lista para configurar sus precios.
          </div>
        ) : (
          <div className="bg-white border border-[#e8e5de] rounded-xl p-6 space-y-6">
            <div className="flex items-center gap-2">
              <MapPin size={15} className="text-[#1c1c1c]"/>
              <div>
                <h3 className="font-serif text-[22px] leading-tight">{selected.name}</h3>
                <p className="text-[11.5px] text-[#8a8579]">{selected.brand} · {selected.address}</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[11.5px] uppercase tracking-wider text-[#8a8579]">Departamento</Label>
                <Select value={dept || 'none'} onValueChange={v => setDept(v === 'none' ? '' : v)}>
                  <SelectTrigger className="mt-1 bg-[#fafaf6]" data-testid="select-dept"><SelectValue placeholder="Sin departamento"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Sin departamento —</SelectItem>
                    {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {deptDoc && (
                  <div className="mt-2 bg-[#f3f0e9] border border-[#e8e5de] rounded-md p-2 text-[11px] text-[#5a5a5a] space-y-0.5">
                    <p>Delay: <b>{deptDoc.delay_hours} h</b> · Depósito: <b>{deptDoc.deposito}</b> · Flete: <b>{deptDoc.diferencial_flete}</b></p>
                    {deptDoc.auto_full && <p>Auto/Full activo · diferencial: <b>+{deptDoc.diferencial_auto_full}</b></p>}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-[11.5px] uppercase tracking-wider text-[#8a8579]">Ventana entre cambios (h)</Label>
                <Input type="number" value={minHours} onChange={e => setMinHours(e.target.value)} className="mt-1 bg-[#fafaf6]" data-testid="input-min-hours"/>
                <p className="text-[10.5px] text-[#8a8579] mt-1">Horas mínimas antes de poder solicitar otro cambio (24 / 48 / …).</p>
              </div>
            </div>

            <div>
              <Label className="text-[11.5px] uppercase tracking-wider text-[#8a8579]">Productos y ajustes específicos</Label>
              <p className="text-[10.5px] text-[#8a8579] mb-2">Activa los que vende esta estación. Los ajustes se suman al precio final (valores negativos para restar).</p>
              <div className="space-y-2">
                {PRODUCTS.map(p => (
                  <div key={p.id} className={`border rounded-lg p-3 transition ${enabled.includes(p.id) ? 'border-[#d4cfc4] bg-[#fafaf6]' : 'border-[#e8e5de] bg-white opacity-60'}`}>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={enabled.includes(p.id)}
                        onChange={() => toggleProduct(p.id)}
                        className="w-4 h-4"
                        data-testid={`product-check-${p.id}`}
                      />
                      <span className="text-[13px] font-medium w-24">{p.label}</span>
                      {enabled.includes(p.id) && (
                        <>
                          <div className="flex items-center gap-1.5">
                            <Label className="text-[10.5px] text-[#8a8579]">Ajuste</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={adj[p.id]?.adjustment ?? 0}
                              onChange={e => setAdj({ ...adj, [p.id]: { ...adj[p.id], adjustment: parseFloat(e.target.value) || 0 } })}
                              className="h-8 w-24 bg-white text-[12px]"
                              data-testid={`input-adj-${p.id}`}
                            />
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Label className="text-[10.5px] text-[#8a8579]">Dif. Auto/Full</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={adj[p.id]?.auto_full_diff ?? 0}
                              onChange={e => setAdj({ ...adj, [p.id]: { ...adj[p.id], auto_full_diff: parseFloat(e.target.value) || 0 } })}
                              className="h-8 w-24 bg-white text-[12px]"
                              data-testid={`input-autofull-${p.id}`}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} className="bg-[#1c1c1c] text-[#f8f6f1] hover:bg-black" data-testid="btn-save-pricing">
                <Save size={13} className="mr-1.5"/> {saving ? 'Guardando…' : 'Guardar configuración'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminStationPricing;
